"use client";

import React from "react";
import { createPortal } from "react-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./msalConfig"; // Ensure you have this config file set up
import bbImage from "../../assests/images/bbb.png";
import InputField from "../shared/InputValidation";
import MessageManager from "../shared/Message";
import appConstants from "../shared/config/constants.json";

import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Building,
  Sparkles,
  Briefcase,
  TrendingUp,
  Loader2,
} from "lucide-react";
import "./signUp.css";
import "./SocialCompletionModal.css";
// import Logo from "../layout/Logo";

import {
  loginApi,
  signUp,
  getGoogleUserProfile,
  getMicrosoftUserProfile,
  commonSignUpApi,
} from "../../services/authService";
import { handleApiError } from "../shared/utils";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

const sanitizeNameInput = (value = "") => value.replace(/[^A-Za-z ]/g, "");

// Separate Modal Component that's completely isolated
const SocialCompletionModal = React.memo(
  ({ isOpen, userData, onComplete, onClose, isLoading }) => {
    const [completionForm, setCompletionForm] = useState({
      name: userData?.name || "",
      email: userData?.email || "",
      // phone: "",
      organization: "",
      website: "",
      // acceptTerms: false,
    });

    const modalRef = useRef(null);
    const { signup } = useAuth();
    const navigate = useNavigate();

    // Update form when userData changes
    useEffect(() => {
      if (userData) {
        setCompletionForm((prev) => ({
          ...prev,
          name: sanitizeNameInput(userData.name || prev.name),
          email: userData.email || prev.email,
        }));
      }
    }, [userData]);

    const handleInputChange = useCallback((e) => {
      const { name, value, type, checked } = e.target;
      const nextValue =
        type === "checkbox"
          ? checked
          : name === "name"
            ? sanitizeNameInput(value)
            : value;

      setCompletionForm((prev) => ({
        ...prev,
        [name]: nextValue,
      }));
    }, []);

    const handleOverlayClick = useCallback(
      (e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      },
      [onClose],
    );

    const handleCloseClick = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      },
      [onClose],
    );

    // Handle escape key
    useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === "Escape" && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        document.body.style.overflow = "hidden";
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "unset";
      };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSocialSignUp = async (e) => {
      e.preventDefault();
      // Set loading state

      try {
        sessionStorage.clear();
        const payload = {
          adminEmail: completionForm.email,
          adminName: completionForm.name,
          adminOrg: completionForm.organization,
          authType: "GOOGLE",
          website: completionForm.website,
        };
        console.log("Submitting Social Sign Up:", payload);
        // debugger;
        const response = await signUp(payload);

        signup(response); // Use the signUp function from AuthContext
        console.log("Sign Up response:", response);

        // Mark as first time user for onboarding
        // sessionStorage.setItem("isFirstTimeUser", response.data.isSetupCompleted);
        navigate("/home");
      } catch (error) {
        console.error("Sign Up error:", error);
        // handleApiError(error, {
        //   showModal: true,
        //   title: "Sign Up Error",
        //   message:
        //     "There was an error completing your sign up. Please try again.",
        // });
      }
    };
    const modalContent = (
      <div className="social-completion-overlay" onClick={handleOverlayClick}>
        <div
          className="social-completion-modal"
          onClick={(e) => e.stopPropagation()}
          ref={modalRef}
        >
          <div className="social-completion-header">
            <div className="social-completion-icon">
              <User size={24} />
            </div>
            <h2>Complete Your Profile</h2>
            <p>Just a few more details to get you started</p>
          </div>

          <form className="social-completion-form">
            <div className="social-completion-columns">
              {/* Left Column - Personal Information */}
              <div className="social-completion-column">
                <div className="social-column-title">
                  <User size={18} />
                  Personal Information
                </div>

                <div className="social-form-group">
                  <label className="social-form-label">
                    <User size={14} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={completionForm.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="social-form-input"
                    pattern="[A-Za-z ]+"
                    title="Name can only contain letters and spaces"
                    required
                  />
                </div>

                <div className="social-form-group">
                  <label className="social-form-label">
                    <Mail size={14} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={completionForm.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className="social-form-input"
                    required
                    readOnly={!!userData?.email}
                  />
                </div>
              </div>

              {/* Right Column - Organization Information */}
              <div className="social-completion-column">
                <div className="social-column-title">
                  <Building size={18} />
                  Organization Details
                </div>

                <div className="social-form-group">
                  <label className="social-form-label">
                    <Building size={14} />
                    Organization Name*
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={completionForm.organization}
                    onChange={handleInputChange}
                    placeholder="Enter your organization name"
                    className="social-form-input"
                    required
                  />
                </div>

                <div className="social-form-group">
                  <label className="social-form-label">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    Website*
                  </label>
                  <input
                    type="url"
                    name="website"
                    required
                    value={completionForm.website}
                    onChange={handleInputChange}
                    placeholder="https://your-company.com"
                    className="social-form-input"
                  />
                </div>
              </div>
            </div>

            <div className="social-completion-actions">
              <button
                type="button"
                onClick={handleCloseClick}
                className="social-btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSocialSignUp}
                className="social-btn-primary"
                disabled={
                  isLoading ||
                  !completionForm.organization ||
                  !completionForm.website
                }
              >
                {isLoading ? (
                  <>
                    <div className="social-spinner"></div>
                    Completing...
                  </>
                ) : (
                  <>
                    Complete Sign Up
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12,5 19,12 12,19" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );

    // Render in portal to completely isolate from parent re-renders
    return typeof document !== "undefined"
      ? createPortal(modalContent, document.body)
      : null;
  },
);

const validatePassword = (password, email, name) => {
  const errors = [];

  // Check minimum length
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter (a-z)");
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter (A-Z)");
  }

  // Check for number
  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number (0-9)");
  }

  // Check for special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("Password must contain at least one special character");
  }

  // Check for 8 consecutive identical characters
  if (/(.)\1{2,}/.test(password)) {
    errors.push(
      "Password cannot contain 3 or more consecutive identical characters",
    );
  }

  // Check if password matches email or name
  if (email && password.toLowerCase() === email.toLowerCase()) {
    errors.push("Password cannot be the same as your email");
  }

  if (name && password.toLowerCase() === name.toLowerCase()) {
    errors.push("Password cannot be the same as your name");
  }

  return errors;
};

const AuthenticationPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { login } = useAuth();
  const { signup } = useAuth();

  const [isSignUpMode, setIsSignUpMode] = useState(true);
  const [isLoggedin, setIsLoggedin] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    password: "",
    website: "",
    checked: false,
  });
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
  });
  const { MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL } = appConstants;

  // Add this temporary state for testing
  const [showCompletionModal, setShowCompletionModal] = useState(false); // Set to true for testing
  const [socialUserData, setSocialUserData] = useState({
    name: "John Doe",
    email: "john@example.com",
  });

  useEffect(() => {
    setIsAnimated(true);
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const handleInputChange = (e) => {
    const nextValue =
      e.target.name === "name"
        ? sanitizeNameInput(e.target.value)
        : e.target.value;

    setFormData({
      ...formData,
      [e.target.name]: nextValue,
    });

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      checked: e.target.checked,
    });
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(
      `${window.location.origin}/terms`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleSocialSignupComplete = useCallback(async (completionData) => {
    setIsLoading(true);
    try {
      console.log("Completion data:", completionData);

      // Here you would normally call your signup API
      // For testing, just simulate success
      setTimeout(() => {
        setShowCompletionModal(false);
        setIsLoading(false);
        console.log("Modal would close and redirect to dashboard");
        // navigate("/home")
      }, 2000);
    } catch (error) {
      console.error("Signup completion error:", error);
      setIsLoading(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCompletionModal(false);
  }, []);

  // API CALLS
  const handleLogin = async (e) => {
    e.preventDefault();
    // Prevent multiple submissions
    if (formData.email === "" || formData.password === "") {
      setShowModal({
        show: true,
        type: "error",
        title: "Missing Fields",
        message: "Please fill in all fields.",
      });
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        authType: "INTERNAL",
      };
      console.log("Submitting Login:", payload);
      // Make API request
      const response = await loginApi(payload);

      login(response); // Use the login function from AuthContext
      // sessionStorage.setItem("isFirstTimeUser", response.data.isSetupCompleted);

      navigate("/home");
    } catch (error) {
      console.error("Login error:", error);
      // handleApiError(error, { showModal: true });
    } finally {
      setIsLoading(false);
    }
  };
  const [showSignInOnly, setShowSignInOnly] = useState(false);
  // https://mayamaya-talent-test-ceeuda5dfabfzet.eastus-01.azurewebsites.net/signin?organization=SMVITM&department=MBA-2025&orgCode=SMVI-2153
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const organization = urlParams.get("organization");
    const department = urlParams.get("department");
    const orgCode = urlParams.get("orgCode");
    const industry = urlParams.get("industry");
    const userRole = urlParams.get("userRole");
    const fieldOfStudy = urlParams.get("fieldOfStudy");
    const educationLevel = urlParams.get("educationLevel");
    if (organization && department && orgCode) {
      sessionStorage.setItem("orgCode", orgCode);
      sessionStorage.setItem("organization", organization);
      sessionStorage.setItem("department", department);
      sessionStorage.setItem("industry", industry);
      sessionStorage.setItem("userRole", userRole);
      sessionStorage.setItem("fieldOfStudy", fieldOfStudy);
      sessionStorage.setItem("educationLevel", educationLevel);

      setShowSignInOnly(true);
      // If all parameters are present, you can use them as needed
      console.log("All parameters are present.");
      const handleLogin = async (e) => {
        e.preventDefault();
        console.log("Login clicked", formData);
      };
    }
  }, []);
  const handleSignupParams = async () => {
    const validationErrors = {};

    const passwordErrors = validatePassword(
      formData.password,
      formData.email,
      formData.name,
    );

    if (passwordErrors.length > 0) {
      validationErrors.password = passwordErrors.join(".,");
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    setErrors({ name: "", email: "", password: "" });
    setIsLoading(true);

    sessionStorage.clear();

    // check the url has organization, department, orgCode
    const urlParams = new URLSearchParams(window.location.search);
    const organization = urlParams.get("organization");
    const department = urlParams.get("department");
    const orgCode = urlParams.get("orgCode");
    const industry = urlParams.get("industry");
    const userRole = urlParams.get("userRole");
    const fieldOfStudy = urlParams.get("fieldOfStudy");
    const educationLevel = urlParams.get("educationLevel");

    try {
      let obj = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        organization: organization,
        department: department,
        authType: "INTERNAL",
        orgCode: orgCode,
        industry: industry,
        userRole: userRole,
        fieldOfStudy: fieldOfStudy,
        educationLevel: educationLevel,
        industry: industry,
        userRole: userRole,
        fieldOfStudy: fieldOfStudy,
        educationLevel: educationLevel,
      };
      const response = await commonSignUpApi(obj);
      if (response.code == 200) {
        sessionStorage.setItem("accessLevel", 0);
        sessionStorage.setItem("email", formData.email);
        sessionStorage.setItem("JWT", response.data.token);

        navigate("/portal-selector");
      }
    } catch (error) {
      console.error("Sign Up error:", error);

      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred during sign up";

      const lowerError = errorMessage.toLowerCase();

      // Map specific errors to form fields
      if (lowerError.includes("invalid encrypted userid or tenantid")) {
        setErrors({
          email: "Enter a valid email address",
          password: "",
          name: "",
        });
      } else if (lowerError.includes("this email is already registered")) {
        setErrors({
          email: "This email is already registered",
          password: "",
          name: "",
        });
      } else {
        // Generic errors show in modal
        handleApiError(error, { showModal: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    sessionStorage.clear();

    e.preventDefault();
    console.log("Sign Up clicked", formData);
    const validationErrors = {};

    const passwordErrors = validatePassword(
      formData.password,
      formData.email,
      formData.name,
    );

    if (passwordErrors.length > 0) {
      // Join multiple errors with comma for InputField to split them
      validationErrors.password = passwordErrors.join(".,");
    }

    // If validation fails, show errors and stop
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return; // Don't proceed to API call
    }

    // Clear errors before API call
    setErrors({ name: "", email: "", password: "" });
    setIsLoading(true);

    // check the url has organization, department, orgCode
    const urlParams = new URLSearchParams(window.location.search);
    const organization = urlParams.get("organization");
    const department = urlParams.get("department");
    const orgCode = urlParams.get("orgCode");
    if (organization && department && orgCode) {
      handleSignupParams();
    } else {
      try {
        let obj = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          authType: "INTERNAL",
        };

        console.log("Submitting Sign Up:", obj);

        const response = await commonSignUpApi(obj);
        if (response.code == 200) {
          sessionStorage.setItem("email", formData.email);
          sessionStorage.setItem("JWT", response.data.token);

          navigate("/type-selection");
        }
        console.log("Sign Up response:", response);
      } catch (error) {
        console.error("Sign Up error:", error);

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "An error occurred during sign up";

        const lowerError = errorMessage.toLowerCase();

        if (lowerError.includes("invalid encrypted userid or tenantid")) {
          setErrors({
            email: "Enter a valid email address",
            password: "",
            name: "",
          });
        } else if (lowerError.includes("this email is already registered")) {
          setErrors({
            email: "This email is already registered",
            password: "",
            name: "",
          });
        } else {
          handleApiError(error, { showModal: true });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getValidQP = (sp, key) => {
    const raw = sp.get(key);
    if (raw == null) return null;
    const v = String(raw).trim();
    if (!v || v.toLowerCase() === "null" || v.toLowerCase() === "undefined")
      return null;
    return v;
  };

  const [socialSignupData, setSocialSignupData] = useState({});
  const isSafari =
    typeof navigator !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Use one stable callback path for every environment
  const redirectUri = `${window.location.origin}/auth/google/callback`;

  const startGoogleAuth = useGoogleLogin({
    flow: "auth-code",

    ux_mode: "popup",
    redirect_uri: redirectUri,
    prompt: "select_account",
    onSuccess: async (tokenResponse) => {
      sessionStorage.clear();

      try {
        // const userInfo = await getGoogleUserProfile(tokenResponse.access_token);

        const sp = new URLSearchParams(window.location.search);
        const organization = getValidQP(sp, "organization");
        const department = getValidQP(sp, "department");
        const orgCode = getValidQP(sp, "orgCode");

        // For UI if needed

        // const ID = userInfo.data.sub;

        if (organization && department && orgCode) {
          // Org-bound flow
          const obj = {
            // name: userInfo.data.name,
            // email: userInfo.data.email,
            organization: organization,
            department: department,
            authType: "GOOGLE",
            orgCode: orgCode,
            // id: ID,
            token: tokenResponse.code,
          };

          const response = await commonSignUpApi(obj);
          if (response?.code === 200) {
            sessionStorage.setItem("accessLevel", "0");
            sessionStorage.setItem("email", response.data.email);
            sessionStorage.setItem("JWT", response.data.token);
            setSocialSignupData({
              email: response.data.email,
              name: response.data.name,
            });
            navigate("/portal-selector");
          } else {
            console.warn("Org flow signup non-200:", response);
            handleApiError(new Error("Signup failed"), { showModal: true });
          }
        } else {
          // No org params → generic flow
          const obj = {
            // name: userInfo.data.name,
            // email: userInfo.data.email,
            authType: "GOOGLE",
            // id: ID,
            token: tokenResponse.code,
          };

          const response = await commonSignUpApi(obj);
          if (response?.code === 200) {
            sessionStorage.setItem("email", response.data.email);
            sessionStorage.setItem("JWT", response.data.token);
            setSocialSignupData({
              email: response.data.email,
              name: response.data.name,
            });
            navigate("/type-selection");
          } else {
            console.warn("Generic flow signup non-200:", response);
            handleApiError(new Error("Signup failed"), { showModal: true });
          }
        }
      } catch (error) {
        console.error("Google login error:", error);
        handleApiError(error, { showModal: true });
      }
    },
    onError: (err) => {
      console.error("Google login failed:", err);
    },
  });

  const loginWithGoogle = useGoogleLogin({
    // Safari may block popups; let it fall back to redirect.
    // Popup remains for Chrome/Edge/Firefox.
    flow: "auth-code",

    ux_mode: isSafari ? "redirect" : "popup",
    // IMPORTANT: must be whitelisted in Google Console (Authorized redirect URIs)
    redirect_uri: redirectUri,
    // Optional: show account chooser each time
    prompt: "select_account",

    onSuccess: async (tokenResponse) => {
      console.log("Google Login SignUp Payload:", tokenResponse);

      try {
        // const userInfo = await getGoogleUserProfile(tokenResponse.access_token);

        const obj = {
          // email: userInfo.data.email,
          authType: "GOOGLE",
          token: tokenResponse.code,
        };

        const response = await commonSignUpApi(obj);
        if (response.code == 200) {
          navigate("/type-selection");
          sessionStorage.setItem("email", response.data.email);
          setSocialSignupData({
            email: response.data.email,
            name: response.data.name,
          });
        }
      } catch (error) {
        console.error("Google login error:", error);
        handleApiError(error, { showModal: true });
      }
    },
  });
  const handleMsAlLogin = async () => {
    sessionStorage.clear();

    const urlParams = new URLSearchParams(window.location.search);
    const organization = urlParams.get("organization");
    const department = urlParams.get("department");
    const orgCode = urlParams.get("orgCode");
    if (organization && department && orgCode) {
      handleMsAlLoginParams();
    } else {
      try {
        // const response = await instance.loginPopup(loginRequest);
        const response = await instance.loginPopup({
          ...loginRequest,
          prompt: "select_account",
        });

        const accessToken = response.accessToken;
        const MsUserProfile = await getMicrosoftUserProfile(accessToken);
        console.log("Microsoft User Profile:", MsUserProfile);
        const MSLoginId = MsUserProfile?.id;

        setSocialSignupData({
          email: MsUserProfile.mail,
          name: MsUserProfile.displayName,
        });
        const obj = {
          email: MsUserProfile.mail,
          authType: "MICROSOFT",
          name: MsUserProfile.displayName,
          id: MSLoginId,
          token: response.idToken,
        };

        const data = await commonSignUpApi(obj);
        if (data.code == 200) {
          navigate("/type-selection");
          sessionStorage.setItem("email", formData.email);
          sessionStorage.setItem("JWT", data.data.token);
        }

        // sessionStorage.setItem("isFirstTimeUser", response.data.isSetupCompleted);
      } catch (error) {
        console.error("MSAL Login error:", error);
        handleApiError(error, { showModal: true });

        // if (error.code == 400 && error.message == "User not found") {
        //   console.log("User not found, showing completion modal");
        //   // Handle user not found case
        //   setSocialUserData({
        //     email: socialSignupData.mail,
        //     name: socialSignupData.displayName,
        //   });
        //   setShowCompletionModal(true);

        //   return;
        // }
      }
    }
  };

  const handleMsAlLoginParams = async () => {
    sessionStorage.clear();

    console.log("Handling MSAL login with params");
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const organization = urlParams.get("organization");
      const department = urlParams.get("department");
      const orgCode = urlParams.get("orgCode");
      // const response = await instance.loginPopup(loginRequest);
      const response = await instance.loginPopup({
        ...loginRequest,
        prompt: "select_account",
      });

      const accessToken = response.accessToken;
      const MsUserProfile = await getMicrosoftUserProfile(accessToken);
      console.log("Microsoft User Profile:", MsUserProfile);
      const MSLoginId = MsUserProfile?.id;

      setSocialSignupData({
        email: MsUserProfile.mail,
        name: MsUserProfile.displayName,
      });
      let obj = {
        name: MsUserProfile.displayName,
        email: MsUserProfile.mail,
        organization: organization,
        department: department,
        authType: "MICROSOFT",
        orgCode: orgCode,
        id: MSLoginId,
        token: response.idToken,
      };

      console.log("Submitting Sign Up:", obj);

      const data = await commonSignUpApi(obj);
      if (data.code == 200) {
        sessionStorage.setItem("accessLevel", 0);
        sessionStorage.setItem("email", MsUserProfile.mail);
        sessionStorage.setItem("JWT", response.data.token);

        navigate("/portal-selector");
      }
      const loginUserDetails = await loginApi(obj);
      if (loginUserDetails.code == 200) {
        login(loginUserDetails); // Use the login function from AuthContext
        navigate("/home");
      }
      // sessionStorage.setItem("isFirstTimeUser", response.data.isSetupCompleted);
    } catch (error) {
      console.error("MSAL Login error:", error);
      handleApiError(error, { showModal: true });

      // if (error.code == 400 && error.message == "User not found") {
      //   console.log("User not found, showing completion modal");
      //   // Handle user not found case
      //   setSocialUserData({
      //     email: socialSignupData.mail,
      //     name: socialSignupData.displayName,
      //   });
      //   setShowCompletionModal(true);

      //   return;
      // }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSignUpMode) {
      handleSignUp(e);
    } else {
      handleLogin(e);
    }
  };

  const handleHelpSupportClick = () => {
    window.open(
      MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <div className="auth-main-container">
      {/* Subtle Background Effects */}
      <div
        className="auth-bg-dynamic"
        style={{
          background: `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, rgba(14, 24, 34, 0.08), transparent 40%)`,
        }}
      ></div>

      <div className="auth-bg-elements">
        <div className="auth-floating-orb auth-orb-1"></div>
        <div className="auth-floating-orb auth-orb-2"></div>
      </div>

      <div className="auth-content-wrapper">
        {/* Left Side - Prominent Image & Content */}
        <div className="auth-showcase-section">
          {/* Prominent Image Display */}
          <div className="auth-image-display">
            <div className="auth-image-frame">
              <img
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/auth-HmXZj5A6u97g3wKvYIeYQvoN9EAw5m.png"
                alt="Talent Console Platform"
                className="auth-main-image"
              />
              <div className="auth-image-glow"></div>
            </div>
          </div>

          {/* Content Below Image */}
          <div className="auth-content-section">
            <div className="auth-brand-header">
              <div className="auth-logo-container">
                {/* <Logo width={70} height={35} className="auth-logo" /> */}
              </div>
              <h2 className="auth-brand-title">Talent Console</h2>
              <p
                className="auth-brand-description"
                style={{ marginBottom: "-8px" }}
              >
                AI-Powered Talent Management Platform
              </p>
            </div>

            <div className="auth-features-list">
              <div className="auth-feature">
                <div className="auth-feature-icon">
                  <Sparkles size={18} />
                </div>
                <div className="auth-feature-content">
                  <h4>AI-Powered Insights</h4>
                  <p>Advanced analytics for talent decisions</p>
                </div>
              </div>
              <div className="auth-feature">
                <div className="auth-feature-icon">
                  <Briefcase size={18} />
                </div>
                <div className="auth-feature-content">
                  <h4>Workforce Planning</h4>
                  <p>Identify future roles & skills gaps</p>
                </div>
              </div>
              <div className="auth-feature">
                <div className="auth-feature-icon">
                  <TrendingUp size={18} />
                </div>
                <div className="auth-feature-content">
                  <h4>Growth Analytics</h4>
                  <p>Track performance & development</p>
                </div>
              </div>
              <div className="securedContainer">
                <button
                  type="button"
                  className="helpSupportBtn"
                  onClick={handleHelpSupportClick}
                >
                  Help & Support
                </button>

                <div className="securedByWrap">
                  <p>Secured by </p>
                  <img src={bbImage} alt="BBB" />
                  <img
                    src="https://piiguard.com/assets/img/pii-guard-logo-white.svg"
                    alt="PII Guard"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Compact Form */}
        <div className="auth-form-section">
          <div
            className={`auth-form-container ${
              isAnimated ? "auth-animate-in" : ""
            }`}
          >
            {/* Form Header */}
            <div className="auth-header">
              <div
                className="logo-container"
                style={{ display: "flex", justifyContent: "center" }}
              >
                <svg
                  width="100"
                  height="50"
                  viewBox="0 0 120 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="career-auth-logo"
                >
                  <path
                    d="M37.0024 0.946045L36.9688 1.02501V1.03354L37.0024 0.946045Z"
                    fill="currentColor"
                  />
                  <path
                    d="M52.4023 23.4972L52.4163 23.5281L52.4302 23.4929L52.4023 23.4972Z"
                    fill="currentColor"
                  />
                  <mask
                    id="mask0_3147_16711"
                    maskUnits="userSpaceOnUse"
                    x="10"
                    y="2"
                    width="100"
                    height="58"
                  >
                    <path
                      d="M110 2.82495H10V59.054H110V2.82495Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0_3147_16711)">
                    <path
                      d="M99.824 7.49986L94.348 23.3389L102.302 45.1929H97.339L92.088 28.8199L86.365 45.1909H81.957C85.804 34.6299 93.532 13.3949 97.379 2.82886H102.306L109.999 23.9599L105.04 23.9709L99.824 7.49986Z"
                      fill="currentColor"
                    />
                    <path
                      d="M89.6626 24.0261L89.6516 24.0581L89.6406 24.0311L89.6626 24.0261Z"
                      fill="currentColor"
                    />
                    <path
                      d="M43.281 7.49195L48.471 23.937L53.43 23.929L45.754 2.82495H40.829C40.788 2.94395 35.638 17.125 35.6 17.241L30.362 2.82495H25.378C21.547 13.391 13.832 34.625 10 45.191H14.408L20.135 28.725L25.36 45.195H29.86L30.091 44.563L35.557 28.817L40.787 45.195H45.749L37.824 23.378C37.838 23.343 43.268 7.52395 43.281 7.49195ZM27.881 38.492C27.873 38.465 22.391 23.409 22.381 23.372L27.844 7.47195L33.109 24.108L27.881 38.492Z"
                      fill="currentColor"
                    />
                    <path
                      d="M63.8158 7.48186L58.3528 23.3249L66.3118 45.1889L61.3528 45.1939L56.1088 28.8249L50.3748 45.1679H45.9648C49.8188 34.5929 57.5358 13.3989 61.3888 2.82886H66.3158L73.9928 23.9289L69.0338 23.9369L63.8158 7.48186Z"
                      fill="currentColor"
                    />
                    <path
                      d="M92.0309 2.87248C86.0059 19.3265 77.5769 42.6405 71.6149 59.0555H67.2419L72.0939 45.2285C72.0939 45.2285 65.3449 28.3135 63.9609 24.1285H68.4609C68.4609 24.1425 74.1109 40.1455 74.1159 40.1635L79.7999 23.9215C77.9999 19.7325 73.0249 7.00148 71.6249 2.84448H76.1249C76.1249 2.85848 81.9469 18.6005 81.9519 18.6185L87.5959 2.87248H92.0309Z"
                      fill="currentColor"
                    />
                  </g>
                </svg>
              </div>
              <h1 className="auth-title">
                {isSignUpMode
                  ? "Sign Up / Create Account"
                  : "Sign In / Welcome Back"}
              </h1>
              <p className="auth-subtitle">
                {isSignUpMode
                  ? "Join thousands of organizations using Talent Console"
                  : "Sign in to access your talent management dashboard"}
              </p>
            </div>

            {/* Social Login */}
            <div className="auth-social-section">
              {!isSignUpMode && (
                <button
                  className="auth-social-btn auth-google-btn"
                  onClick={startGoogleAuth}
                  disabled={isLoading}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </div>
                  <span>Continue with Google</span>
                </button>
              )}

              {!isSignUpMode && (
                <button
                  className="auth-social-btn auth-microsoft-btn"
                  onClick={handleMsAlLogin}
                  disabled={isLoading}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M1 1h10v10H1z" />
                      <path fill="#00A4EF" d="M13 1h10v10H13z" />
                      <path fill="#7FBA00" d="M1 13h10v10H1z" />
                      <path fill="#FFB900" d="M13 13h10v10H13z" />
                    </svg>
                  </div>
                  <span>Continue with Microsoft</span>
                </button>
              )}

              {/* Social Signup */}
              {isSignUpMode && (
                <button
                  className="auth-social-btn auth-google-btn"
                  disabled={isLoading}
                  onClick={startGoogleAuth}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </div>
                  <span>Sign Up with Google</span>
                </button>
              )}

              {isSignUpMode && (
                <button
                  className="auth-social-btn auth-microsoft-btn"
                  disabled={isLoading}
                  onClick={handleMsAlLogin}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M1 1h10v10H1z" />
                      <path fill="#00A4EF" d="M13 1h10v10H13z" />
                      <path fill="#7FBA00" d="M1 13h10v10H1z" />
                      <path fill="#FFB900" d="M13 13h10v10H13z" />
                    </svg>
                  </div>
                  <span>Sign Up with Microsoft</span>
                </button>
              )}
            </div>

            <div className="auth-divider">
              <div className="auth-divider-line"></div>
              <span className="auth-divider-text">or</span>
              <div className="auth-divider-line"></div>
            </div>

            {/* Main Form */}
            <form onSubmit={handleSubmit} className="auth-form">
              {isSignUpMode && (
                <div className="auth-field">
                  {/* <label htmlFor="name" className="auth-label">
                    <User size={14} />
                    <span>
                      Full Name<span style={{ color: "red" }}>*</span>
                    </span>
                  </label> */}
                  <InputField
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                    error={errors.name}
                    icon={User}
                    labelTooltipText="Full name cannot be changed after setup. To request a change later, you must submit a support form"
                    pattern="[A-Za-z ]+"
                    title="Name can only contain letters and spaces"
                  />
                </div>
              )}

              <div className="auth-field">
                {/* <label htmlFor="email" className="auth-label">
                  <Mail size={14} />
                  <span>
                    Email Address<span style={{ color: "red" }}>*</span>
                  </span>
                </label> */}
                <InputField
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required
                  error={errors.email}
                  icon={Mail}
                  showPasswordToggle={false}
                />
              </div>

              {isSignUpMode && (
                <>
                  <div className="auth-field">
                    {/* <label htmlFor="password" className="auth-label">
                      <Lock size={14} />
                      <span>
                        Password<span style={{ color: "red" }}>*</span>
                      </span>
                    </label>
                    <div className="auth-password-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        required
                        className="auth-input"
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div> */}
                    <InputField
                      label="Password"
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      required
                      error={errors.password}
                      icon={Lock}
                      showPasswordToggle={true}
                    />
                  </div>
                  <label className="auth-checkbox">
                    <input
                      type="checkbox"
                      required
                      value={formData.checked}
                      onChange={handleCheckboxChange}
                      checked={formData.checked}
                    />
                    <span className="auth-checkmark"></span>
                    <div onClick={handleTermsClick}>
                      <span style={{ fontSize: "12px" }}>
                        Terms & Conditions
                      </span>
                    </div>
                  </label>
                </>
              )}

              {!isSignUpMode && (
                <div className="auth-field">
                  <label htmlFor="password" className="auth-label">
                    <Lock size={14} />
                    <span>
                      Password<span style={{ color: "red" }}>*</span>
                    </span>
                  </label>
                  <div className="auth-password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      required
                      className="auth-input"
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              )}

              {!isSignUpMode && (
                <div className="auth-options">
                  <label className="auth-checkbox">
                    <input type="checkbox" />
                    <span className="auth-checkmark"></span>
                    <span>Remember me</span>
                  </label>
                  <a href="#" className="auth-forgot">
                    Forgot Password?
                  </a>
                </div>
              )}

              {/* {isSignUpMode ? (
                <button className="auth-submit" onClick={handleSignUp}>
                  Sign Up
                </button>
              ) : (
                <button className="auth-submit" onClick={handleLogin}>
                  Sign In
                </button>
              )} */}

              {isSignUpMode ? (
                <button
                  className="auth-submit"
                  onClick={handleSignUp}
                  disabled={
                    isLoading ||
                    !formData.checked ||
                    !formData.name.trim() ||
                    !formData.email.trim() ||
                    !formData.password.trim()
                  } // Prevent multiple clicks
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="spin" size={16} />
                      Signing Up...
                    </>
                  ) : (
                    "Sign Up"
                  )}
                </button>
              ) : (
                <button
                  className="auth-submit"
                  onClick={handleLogin}
                  disabled={
                    isLoading ||
                    !formData.email.trim() ||
                    !formData.password.trim()
                  }
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="spin" size={16} />
                      Signing In...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              )}
            </form>

            {/* Form Footer */}

            <div className="auth-footer">
              <p className="auth-footer-text">
                {isSignUpMode
                  ? "Already have an account?"
                  : "Don't have an account?"}{" "}
                <button
                  type="button"
                  className="auth-switch"
                  onClick={() => navigate("/signin")}
                  // onClick={() => setIsSignUpMode(!isSignUpMode)}
                >
                  {isSignUpMode ? "Sign In" : "Sign Up"}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Social Completion Modal - Now rendered in portal */}
      {/* <SocialCompletionModal
        isOpen={showCompletionModal}
        userData={socialSignupData}
        onComplete={handleSocialSignupComplete}
        onClose={handleCloseModal}
        isLoading={isLoading}
      /> */}
    </div>
  );
};

export default AuthenticationPage;
